import {
  type ApiToolCallModel,
  type Modality,
  ModalityType,
} from '@littlebird/sdk';
import {
  toolMatchKeyForCorrelatableModality,
  type ReasoningModalityLike,
} from '@littlebird/ui-utils';

export type ToolKind = 'read' | 'write' | 'edit' | 'shell' | 'other';

export interface ToolResultPayload {
  text: string;
  isError: boolean;
}

export interface ToolDescriptor {
  kind: ToolKind;
  toolName: string;
  args: Record<string, unknown>;
  result?: ToolResultPayload;
}

const KIND_BY_MODALITY_TYPE: Partial<Record<ModalityType, ToolKind>> = {
  [ModalityType.ReadFile]: 'read',
  [ModalityType.WriteFile]: 'write',
  [ModalityType.EditFile]: 'edit',
  [ModalityType.Bash]: 'shell',
  [ModalityType.RunSandbox]: 'shell',
};

export function kindForModalityType(type: ModalityType): ToolKind {
  return KIND_BY_MODALITY_TYPE[type] ?? 'other';
}

interface ToolMatchKey {
  toolName: string;
  argKey: string;
  argValue: string;
}

export function toolMatchKeyForModality(
  modality: Modality
): ToolMatchKey | undefined {
  return toolMatchKeyForCorrelatableModality(
    modality as unknown as ReasoningModalityLike
  );
}

export function descriptorFromModality(
  modality: Modality,
  toolCall?: ApiToolCallModel
): ToolDescriptor | undefined {
  const matchKey = toolMatchKeyForModality(modality);
  if (!matchKey) return undefined;

  // Modality-only args (content, old_string, new_string) aren't always
  // echoed onto the tool call. Merge them so the preview renders pre-result —
  // and clone so we never write into the observable tool-call arguments.
  let args: Record<string, unknown>;
  switch (modality.type) {
    case ModalityType.WriteFile:
      args = {
        ...(toolCall?.arguments ?? {}),
        [matchKey.argKey]: matchKey.argValue,
      };
      args.content = modality.content;
      args.mode = modality.mode;
      break;
    case ModalityType.EditFile:
      args = {
        ...(toolCall?.arguments ?? {}),
        [matchKey.argKey]: matchKey.argValue,
      };
      args.old_string = modality.old_string;
      args.new_string = modality.new_string;
      break;
    default:
      args = toolCall?.arguments ?? { [matchKey.argKey]: matchKey.argValue };
  }

  const result =
    toolCall && toolCall.result !== null && toolCall.result !== undefined
      ? {
          text: toolCall.result,
          isError: toolCall.status === 'error' || toolCall.status === 'denied',
        }
      : undefined;

  return {
    kind: kindForModalityType(modality.type),
    toolName: matchKey.toolName,
    args,
    result,
  };
}
